import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { BLACKBODY_CHUNK } from '../visualizers/ember.js';

// PyroLayer - the pyrotechnics engine, extracted VERBATIM from
// visualizers/pyro.js so that main-stage.js can fire the same gerbs, comets
// and wall of fire without rewriting them. Nothing about the physics, the
// crackle, the blackbody shading or the emission schedule is new here: every
// number and every comment below is Pyro's own, and Pyro renders identically
// through this module (verified by stepping the old and the new class through
// the same seeded run and comparing every particle buffer).
//
// What IS new is the SPACE the layer can render in:
//
//   'clip'   Pyro's own: x in [-aspect, aspect], y in [-1, 1], written
//            straight to clip space, no depth. The shaders are Pyro's text.
//   'world'  the simulation runs in exactly the same 2D units, but every
//            vertex is placed on a vertical plane in a 3D scene -
//            world = (uWorld.xy + (x, y - GROUND_Y) * uWorld.w, uWorld.z) -
//            and goes through the camera. Points scale with 1/depth, the
//            safe zone is read from the projected position, fog is applied
//            by hand (Three injects none into a ShaderMaterial), and the
//            depth test is ON, so anything opaque nearer the camera - a
//            crowd's silhouettes - occludes the sparks for free.
//
// The cadence (what fires when) is NOT here. Pyro keeps its beat/bar/phrase
// schedule and its ring of nozzles; MainStage has its own, sparser one. The
// layer only knows how to fire a thing at a nozzle and how to run it.

// --- the physics -------------------------------------------------------------
//
// A GERB IS NOT A BALLISTIC ARC. A spark leaves the nozzle at high speed and is
// stopped by air, not by gravity: it decelerates hard over the first third of
// its flight, hangs, and falls slowly. That is the shape of a gerb, and drag is
// what produces it - with gravity alone the same particle draws a parabola and
// the whole thing reads as a fountain in a cartoon.
//
// Quadratic drag, because that is what air does at these speeds, and because it
// gives a closed form to size the launch velocity against:
//     apex = ln(1 + DRAG * v0^2 / G) / (2 * DRAG)
//
// WHAT MATTERS IS v0 AGAINST TERMINAL VELOCITY, sqrt(G / DRAG), and getting
// that ratio wrong is how a gerb turns back into a parabola without anything
// looking obviously broken. At DRAG = 0.70 the terminal velocity is 1.69
// against launch speeds of 1.45 to 2.54: drag is present but never dominant,
// and measured, the rise took 83 frames and the fall 86 - a ratio of 1.04,
// which IS a ballistic parabola. Drag has to kill the launch speed, not merely
// trim it.
//
// At DRAG = 1.6 terminal velocity is 1.12 and a beat gerb launches at 3.86:
// a ratio of 3.4, the grain is stopped in the first third of its flight and
// drifts down at terminal speed afterwards. That asymmetry is the shape.
const G = 2.0;
const DRAG = 1.6;
// THE SPREAD IS THE SHAPE. The brief's 8 to 14 is a ratio of 1.75 between the
// slowest and fastest grain in one charge, and that ratio is not decoration:
// with a narrow spread every grain flies the same trajectory and the gerb
// travels as one compact clump with nothing left at the nozzle. Widened to
// 1.75, the slow grains stay low while the fast ones reach the apex and the
// charge reads as a plume. Measured apex with these numbers: 42% of the frame
// above the nozzle for a beat, 68% for a phrase.
// Set from the RUNNING SIMULATION, not from the formula: the closed form
// assumes a vertical launch from rest, and between the 12-degree cone and the
// gravity a grain takes during the 120 ms emission, the measured apex comes out
// about a tenth below it.
// Apex is ln(1 + DRAG*v0^2/G) / (2*DRAG), so height costs velocity steeply -
// reaching twice as high takes roughly four times the launch speed, and there
// is no way to make a gerb much bigger cheaply.
//
// MEASURED on one charge fired alone, with the rest of the show silenced (the
// apex of the highest live grain, as a fraction of the frame above the nozzle).
// The closed form runs about a sixth high at these speeds, so these are the
// simulation's numbers, not the formula's:
//                    before   after
//   beat gerb         43%      64%
//   phrase gerb       68%      84%
// and one plume goes from 31% of the frame wide to 37%, so a single charge
// covers about 1.8 times the area it used to.
//
// 13.5 AND NOT 16, WHICH WAS TRIED. Speed buys height but spends it on area,
// and the grains are a fixed budget: at 16 the apex reached 67% and the plume
// thinned out - the fraction of the frame lit above the spark threshold FELL,
// from 0.072 to 0.063. A taller, sparser plume does not read as bigger, it
// reads as weaker. The sweep, every row at half the old launch rate:
//   v=11.0 n=1100   lit 0.058   apex 61%
//   v=13.5 n=1400   lit 0.072   apex 64%
//   v=16.0 n=1700   lit 0.063   apex 67%
//   v=13.5 n=1800   lit 0.080   apex 64%   <- this one
// against lit 0.059 and apex 43% before.
// The 1.75 ratio between the slowest and fastest grain in a charge is kept - it
// is what makes a gerb a plume rather than a clump.
export const V_BEAT = [7.71, 13.50];
export const V_PHRASE = [14.00, 24.50];
export const V_WALL = [13.00, 22.75];
const CONE_DEG = 12.0;           // half-angle of the jet
export const NOZZLE_TILT = 0.16; // radians, leaning outward from centre

export const LIFE_MIN = 1.5;
// 3.6, not 2.5. A grain that now climbs to 1.3 world units falls back at a
// terminal velocity of 1.12, so the round trip takes about 2.2 s on its own -
// at the old ceiling the big gerbs were being extinguished in mid-air at the
// top of their arc, which reads as the plume being cut off rather than as it
// falling.
export const LIFE_MAX = 3.6;
const EMIT_MS = 120;             // a gerb's emission window
// 1800, where this was 380 and the brief asked for 200. Half as many gerbs
// fire, so each one can afford nearly five times the grains - and density is
// half of what "bigger" means: the same plume drawn with 380 grains over twice
// the area is a thinner plume, not a larger one. Measured with drops firing,
// the standing count peaks at 6785 against the tier-2 budget of 8000, so the
// ring buffer never wraps and overwrites a plume that is still burning.
export const BEAT_PARTICLES = 1800;

// --- the crackle -------------------------------------------------------------
//
// Titanium does not fade out, it CRACKLES: near the end of its burn a grain
// fragments into two or three brief, very bright flashes. It is a small amount
// of code and it is the single detail that separates this from a cartoon
// firework - see `?pyrocrackle=0` to watch the difference rather than take it
// on trust.
const CRACKLE_AT = 0.70;         // fraction of life elapsed
const CRACKLE_CHANCE = 0.333;
const CRACKLE_CHILDREN = 2;
const CRACKLE_SPEED = 0.55;
const CRACKLE_LIFE = [0.10, 0.26];
const CRACKLE_FLARE = 1.6;       // how much brighter a fragment burns

// --- comets ------------------------------------------------------------------
const COMET_RISE = [0.8, 1.4];   // seconds of constant-speed climb
const COMET_TRAIL_PER_S = 60 / 1.1;
const COMET_BURST = 280;
const COMET_BURST_V = [1.35, 3.15];

// --- shells ------------------------------------------------------------------
//
// A COMET IS NOT A FIREWORK. A comet is a single coloured star climbing off the
// deck and popping - stage hardware. A shell is launched high, bursts wide and
// HANGS, and its shape is the point: the four here are the four a real display
// actually fires, and they are told apart by how the stars are aimed and how
// long they burn, not by colour.
//
//   pivoine    stars radially at one speed, short burn, no trails: a clean
//              sphere that expands and goes out
//   saule      slow stars with a LONG burn and heavy droop; they shed trail
//              sparks as they fall, which is the drooping golden curtain
//   anneau     stars on a circle at a very narrow speed spread, so the burst
//              stays a ring instead of filling in
//   palmier    a handful of thick fronds rather than a uniform sphere, each a
//              tight cluster that climbs and bends over
export const SHELL_SHAPES = ['pivoine', 'saule', 'anneau', 'palmier'];
const SHELL_RISE = [1.0, 1.5];     // seconds climbing before the break
const SHELL_APEX = [0.18, 0.72];   // where it breaks, in world y
// Star speeds up by about a third and counts with them: a shell has to hold
// the frame on its own now that it is not competing with three gerbs, and a
// burst that does not reach the edges reads as small however bright it is.
const SHELL_STARS = {
  pivoine:  { n: 300, v: [1.85, 2.90], life: [1.3, 2.1], droop: 1.0, trail: 0 },
  // trail is PER STAR PER SECOND, and at 26 it was the single biggest source
  // of light in the piece: 150 stars shedding 26 a second is close to four
  // thousand new particles a second, all inside one burst, which saturates
  // additively and then blooms across the whole frame. Measured over seven
  // draws of the same thirty-two bars, it swung the mean luminance 4.5x while
  // the total particle count moved less than 3%.
  saule:    { n: 180, v: [1.20, 1.75], life: [2.8, 4.2], droop: 2.1, trail: 6 },
  anneau:   { n: 210, v: [2.38, 2.55], life: [1.4, 2.2], droop: 0.9, trail: 0 },
  palmier:  { n: 250, v: [1.82, 3.22], life: [2.0, 3.2], droop: 1.7, trail: 4 }
};
const PALM_FRONDS = 9;
const SHELL_TRAIL_PER_S = 34;      // the rising shell's own tail
// The title bouquet (option `title`): free flight, then pulled onto the
// lettering, then the fall once let go - seconds from the break.
const TITLE_FREE = 0.25;
const TITLE_FORMED = 0.9;
const TITLE_FALL = 1.5;

// --- flame columns -----------------------------------------------------------
//
// NOT PARTICLES. A flame is a continuous luminous body, and made of sprites it
// reads as a swarm of embers however many you use. One quad per column with a
// noise field in it is both cheaper and right: the column narrows and tears at
// the top, and its temperature falls with height through the same blackbody
// curve as the sparks, so the two layers belong to the same fire.
const FLAME_SLOTS = 6;
const FLAME_LIFE = [1.1, 1.9];
// The QUAD's height, not the flame's. The tip tears off around 0.55 of the way
// up, so a column that should stand a world unit tall needs a quad closer to
// 1.8 - measured on screen, not assumed.
const FLAME_HEIGHT = [0.95, 1.70];
// 0.44, not 0.30. At the narrower width and with the taper it had, the column
// came out as a candle flame: a smooth needle. A stage gas jet is a wide,
// ragged, snaking body - the width and the lateral noise are what say "jet"
// rather than "wick".
const FLAME_WIDTH = 0.44;

// --- the wall ----------------------------------------------------------------
export const WALL_NOZZLES = 12;
const WALL_MS = 600;
export const WALL_PARTICLES = 3200;

// --- smoke -------------------------------------------------------------------
const SMOKE_MAX = 300;
const SMOKE_RISE = 0.055;
const SMOKE_LIFE = [9.0, 17.0];
const SMOKE_CAP = 0.85;          // ceiling on accumulated density

const SAFE_FLOOR = 0.50;
// How a zoomed grain is dimmed: pow(zoom, this). -2 would keep each grain's
// total light; -1.2 lets a closer charge be brighter overall, as it should be.
const GRAIN_ZOOM_DIM = -1.2;
const FLAME_ZOOM_DIM = -1.0;
export const GROUND_Y = -0.92;   // where the nozzles sit
export const GLOW_SLOTS = 6;     // simultaneous ambient glows the background takes

// The reference budget every count is scaled against - Pyro's tier 2. A layer
// with a smaller cap fires proportionally fewer grains per charge, which is
// what keeps a plume a plume rather than a ring buffer overwriting itself.
export const REFERENCE_MAX = 8000;

// External light sources a world-space smoke can be lit by (a stage rig's
// beams). Sized for stage-light-rig's HEAD_COUNT; a consumer with fewer
// leaves the rest black.
const WORLD_LIGHTS = 5;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }

// =============================================================================
// SHADERS
// =============================================================================

// Placement of a simulation point. The clip text is Pyro's, unchanged; the
// world text places the same point on the layer's plane and projects it.
// uZoom = (pivot x, pivot y, factor): the clip view brought CLOSER. Every
// placement is scaled about the pivot and every grain grows with it - the
// simulation itself is untouched, and a consumer keeps its nozzles where it
// wants them on screen by placing them at fromScreen() (see Pyro). Factor 1
// is the layer as it always was; MainStage's world mode never reads it.
const CLIP_PLACE = `
    p = uZoom.xy + (p - uZoom.xy) * uZoom.z;
    vec2 q = abs(p - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, 0.30, sd));

    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    gl_PointSize = aSize * uPixel * uZoom.z * mix(1.0, 1.5, uMirror);
    // A grain zoom times wider covers zoom^2 the pixels: dimmed per pixel so
    // the closer view reads as BIGGER, not as a frame washed out through the
    // bloom (measured: mean 62 -> 108 on 255 uncompensated).
    vFade *= pow(uZoom.z, ${GRAIN_ZOOM_DIM.toFixed(2)});
`;

// uWorld = (origin x, origin y, plane z, world units per simulation unit),
// uDepth = (reference view depth the point size was computed for, fog
// density). The safe zone is Pyro's own ramp, read from the PROJECTED position
// so the cover attenuates the sparks wherever the camera happens to put them.
const WORLD_UNIFORMS = `
  uniform vec4 uWorld;
  uniform vec2 uDepth;
`;
const WORLD_PLACE = `
    vec3 wp = vec3(uWorld.x + p.x * uWorld.w, uWorld.y + (p.y - ${GROUND_Y.toFixed(2)}) * uWorld.w, uWorld.z);
    vec4 mv = modelViewMatrix * vec4(wp, 1.0);
    gl_Position = projectionMatrix * mv;
    float depth = max(-mv.z, 0.001);
    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);
    vec2 q = abs(vec2(ndc.x * uAspect, ndc.y) - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, 0.30, sd));
    float fog = uDepth.y * depth;
    vFade *= exp(-fog * fog);
    gl_PointSize = aSize * uPixel * mix(1.0, 1.5, uMirror) * uDepth.x / depth;
`;

// THE TITLE EXTENSION (option `title`, Shapes): two things a plain layer
// does not have, compiled in only when asked for so every other consumer's
// shader text stays exactly what it was.
//   aSlot   per-particle colour slot (0..3) into uPal: a bouquet keeps the
//           colour it was fired with even when the next one is another.
//   aTitle  -1, or this star's index into uTitleTex - its place in the
//           title. The CPU flies the star as an ordinary shell star; the
//           vertex shader pulls it onto its target by uTitle.x (the whole
//           title bursts at once, so one uniform carries the schedule).
// The targets are a data texture filled ONCE per track (setTitleTargets);
// per frame only the uniform moves.
const TITLE_VERTEX_DECL = `
  attribute float aSlot;
  attribute float aTitle;
  uniform highp sampler2D uTitleTex;
  uniform vec2 uTitleTexSize;
  uniform vec4 uTitle;          // x pull 0..1, y time, z twinkle amount, w hold 0..1
  varying float vSlot;
  varying float vTitleStar;     // 1 = a star of the title
  varying float vHold;          // 1 while it is part of the lettering
  varying float vTwinkle;
`;
const TITLE_VERTEX_PULL = `
    vSlot = aSlot;
    vTitleStar = step(-0.5, aTitle);
    vHold = 0.0; vTwinkle = 1.0;
    if (aTitle > -0.5) {
      float ti = aTitle;
      vec2 tuv = vec2((mod(ti, uTitleTexSize.x) + 0.5) / uTitleTexSize.x,
                      (floor(ti / uTitleTexSize.x) + 0.5) / uTitleTexSize.y);
      vec2 target = texture2D(uTitleTex, tuv).xy;
      p = mix(p, target, uTitle.x);
      vHold = uTitle.w;
      // each star twinkles on its own: a hash of its index and a 14 Hz step
      float h = fract(sin(ti * 12.9898 + floor(uTitle.y * 14.0) * 78.233) * 43758.5453);
      vTwinkle = 1.0 + uTitle.z * (h - 0.5) * 1.4;
    }
`;

function sparkVertex(world, title) {
  return `
  precision ${title ? 'highp' : 'mediump'} float;
  attribute float aLife;        // 1 at the nozzle, 0 at burnout
  attribute float aSize;
  attribute float aKind;        // 0 = gerb spark, 1 = comet, 2 = shell star
  attribute float aFlare;       // crackle fragments burn brighter
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uPixel;
  uniform vec3 uZoom;
  uniform float uMirror;        // 1 = the floor reflection pass
  ${world ? WORLD_UNIFORMS : ''}
  varying float vLife;
  varying float vKind;
  varying float vFlare;
  varying float vSafe;
  varying float vFade;
  ${title ? TITLE_VERTEX_DECL : ''}

  void main() {
    vLife = aLife; vKind = aKind; vFlare = aFlare;
    vec2 p = position.xy;
    ${title ? TITLE_VERTEX_PULL : ''}
    // The reflection: the same particles, folded under the stage line. Drawn
    // from the same buffer rather than a second simulation, which is the whole
    // reason it costs one extra draw call and nothing else.
    //
    // AND IT FADES WITH DEPTH. A reflection in a deck is not a copy - it dims
    // and spreads the further the source is from the surface. Without that
    // fade every falling spark piles its mirror image into the bottom strip of
    // the frame, and because the mirror points are 1.9x wider - 3.6x the area -
    // at additive blending the reflection radiates as much total light as the
    // sparks do. Measured: it took the frame's mean luminance from 35 to 222
    // and put a third of the pixels at full white.
    float above = max(p.y - ${GROUND_Y.toFixed(2)}, 0.0);
    vFade = mix(1.0, exp(-above * 2.6), uMirror);
    p.y = mix(p.y, 2.0 * ${GROUND_Y.toFixed(2)} - p.y, uMirror);
${world ? WORLD_PLACE : CLIP_PLACE}
  }
`;
}

function sparkFragment(mirror, title) {
  return `
  precision mediump float;
  uniform vec3 uCometCol;
  uniform vec3 uShellCol;
  uniform float uPeak;
  ${title ? `uniform vec3 uPal[4];
  uniform float uTitleGain;
  varying float vSlot;
  varying float vTitleStar;
  varying float vHold;
  varying float vTwinkle;` : ''}
  varying float vLife;
  varying float vKind;
  varying float vFlare;
  varying float vSafe;
  varying float vFade;

${BLACKBODY_CHUNK}

  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    // A soft core with a hot centre. Points are square; this is what makes them
    // read as glowing grains rather than as sprites.
    float a = exp(-r2 * ${mirror ? '9.0' : '16.0'}) ;

    // TEMPERATURE FALLS ALONG THE LIFE, and not linearly: a grain spends most
    // of its flight yellow and only the last moments red, so the curve is bent
    // to hold the middle of the ramp.
    float t = pow(clamp(vLife, 0.0, 1.0), 0.55);
    vec3 col = blackbody(t);
    // The blue-white at the nozzle. Ember's curve tops out at neutral white by
    // construction, so the coldest-looking, hottest end is added here rather
    // than by editing a function three other pieces depend on.
    col += vec3(0.0, 0.06, 0.30) * smoothstep(0.88, 1.0, t);

    // The two layers allowed the palette, and they take DIFFERENT entries from
    // it. A comet is a coloured star off the deck; a shell star is a chemical
    // colour high in the frame. Three colours in the air at once - white gerbs,
    // one comet hue, one shell hue - is what stops a busy frame reading as a
    // single wash.
    // A COLOURED STAR STILL GOES OUT. These had a floor - 0.35 and 0.30 of a
    // fully saturated hue at zero life - so a comet or a shell star stayed lit
    // right up to the frame it vanished, while a blackbody spark faded to
    // nothing. Two shells alone then carried two thirds of the frame's light:
    // measured on a draw that saturated, removing them took the mean luminance
    // from 201.8 to 68.8 and the blown pixels from 19.1% to 1.4%. The extra
    // factor of t is the fade the chemical colours were missing.
    float fade = t * (0.30 + 0.85 * t);
    float isComet = step(0.5, vKind) * step(vKind, 1.5);
    float isShell = step(1.5, vKind);
    ${title ? `
    // the colour of the slot it was fired with, for comets and shell stars
    int si = int(vSlot + 0.5);
    vec3 slotCol = si == 0 ? uPal[0] : si == 1 ? uPal[1] : si == 2 ? uPal[2] : uPal[3];
    col = mix(col, slotCol * fade, isComet);
    col = mix(col, slotCol * fade, isShell);
    // a title star: warm white while it writes, its accent over a golden
    // blackbody once it falls
    // (1 200 stars where a peony has 300: the free burst and the rain are
    // dimmed in proportion, or the break is a white ball that tints the frame)
    vec3 rain = mix(blackbody(0.62 + 0.3 * t), slotCol, 0.3) * fade * 0.45;
    vec3 titleCol = mix(rain, vec3(1.0, 0.86, 0.66) * 2.2 * vTwinkle, vHold);
    col = mix(col, titleCol, vTitleStar);
    ` : `
    col = mix(col, uCometCol * fade, isComet);
    col = mix(col, uShellCol * fade, isShell);
    `}
    // A GRAIN IS NOT A LIGHT SOURCE AT FULL SCALE. uPeak is the brightness the
    // brief wants a gerb to REACH so it blooms hard - but that is the value of
    // a dense cluster of grains on top of each other, not of one grain. Emitted
    // at full peak, two hundred sparks in a 120 ms burst saturate to white
    // before the bloom threshold even sees them, and the whole frame turns into
    // a beige haze with no structure in it at all.
    // 0.50, and NOT lower - this was tried. The frame's brightness varies a lot
    // from bar to bar: over seven draws of the same thirty-two bars the mean
    // luminance ran 27 to 202 with the particle count steady to within 5%.
    // Masking each layer in turn on the worst draw puts it on the sparks (202
    // against 84 with the Points hidden), but cutting this gain by 28% moved
    // that draw only 6% - the pile-up is already several times over white, so
    // scaling it down does not unclip it - while darkening every quiet frame by
    // a quarter. The variance is density, not per-grain brightness, and the
    // place to spend on it would be the emission schedule.
    col *= ${mirror ? '0.09' : '0.50'} * (1.0 + vFlare) * vFade;
    ${title ? `
    // the title never goes behind the cover (it is placed above it), and it
    // has its own gain: the layer is dimmed under the shapes, the title not
    gl_FragColor = vec4(col * mix(uPeak * vSafe, uTitleGain, vTitleStar), a);` : `
    gl_FragColor = vec4(col * uPeak * vSafe, a);`}
  }
`;
}

// THE PUFF IS LIT PER SPRITE, NOT PER PIXEL. A smoke sprite is a hundred
// pixels across and there are three hundred of them: evaluating eight glow
// sources inside the fragment shader is eight distance computations over three
// million pixels, and measured on the Pi that alone was 5.9 ms of a 31.2 ms
// frame - more than the trails, the reflection and the sparks together. A puff
// is small enough that one lighting value for the whole sprite is
// indistinguishable, and it moves the same work to three hundred vertices.
//
// In world space the puff is ALSO lit by external beams - a stage rig's
// heads, given as a ray each (origin, direction, colour). The distance from
// the puff to the ray is the same per-vertex arithmetic as the glow's, and a
// puff a beam passes through lights up the way a real haze does, which is the
// only reason a beam is visible in a real room at all.
function smokeVertex(world) {
  return `
  precision mediump float;
  attribute float aLife;
  attribute float aSize;
  uniform float uAspect;
  uniform float uPixel;
  uniform vec3 uZoom;
  uniform vec3 uSmokeCol;
  uniform vec3 uGlowPos[${GLOW_SLOTS}];
  ${world ? `${WORLD_UNIFORMS}
  uniform vec3 uLightA[${WORLD_LIGHTS}];
  uniform vec3 uLightD[${WORLD_LIGHTS}];
  uniform vec3 uLightC[${WORLD_LIGHTS}];` : ''}
  varying float vLife;
  varying vec3 vCol;
  void main() {
    vLife = aLife;
    vec3 lit = vec3(0.0);
    for (int i = 0; i < ${GLOW_SLOTS}; i++) {
      vec3 g = uGlowPos[i];
      if (g.z <= 0.0) continue;
      float dist = length(position.xy - g.xy);
      // Strong falloff: a burning gerb should light the puff it is inside, not
      // the whole stage. Soft-clamped for the same reason as the background's.
      lit += vec3(1.0, 0.62, 0.26) * g.z * 0.40 / (1.0 + dist * dist * 55.0);
    }
${world ? `
    vec3 wp = vec3(uWorld.x + position.x * uWorld.w, uWorld.y + (position.y - ${GROUND_Y.toFixed(2)}) * uWorld.w, uWorld.z);
    for (int i = 0; i < ${WORLD_LIGHTS}; i++) {
      vec3 rel = wp - uLightA[i];
      float along = dot(rel, uLightD[i]);
      // Only in front of the head, and only near the beam's axis: the beam is
      // a narrow cone, and a puff two units off it is in the dark.
      float off = length(rel - uLightD[i] * along);
      lit += uLightC[i] * step(0.0, along) / (1.0 + off * off * 0.9);
    }
    vCol = uSmokeCol * 0.14 + lit / (1.0 + max(lit.r, max(lit.g, lit.b)));
    vec4 mv = modelViewMatrix * vec4(wp, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = aSize * uPixel * uDepth.x / max(-mv.z, 0.001);
` : `
    vCol = uSmokeCol * 0.14 + lit / (1.0 + max(lit.r, max(lit.g, lit.b)));
    vec2 zp = uZoom.xy + (position.xy - uZoom.xy) * uZoom.z;
    gl_Position = vec4(zp.x / uAspect, zp.y, 0.0, 1.0);
    gl_PointSize = aSize * uPixel * uZoom.z;
`}
  }
`;
}

const SMOKE_FRAGMENT = `
  precision mediump float;
  uniform float uDensity;
  varying float vLife;
  varying vec3 vCol;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    // 0.055, not 0.5. Three hundred sprites eighty pixels across overlap ten
    // deep near the nozzles; at anything above about a twentieth each, the
    // stage disappears behind a flat beige wall and every spark lands on top
    // of it instead of in front of black.
    float a = exp(-r2 * 7.0) * 0.055;
    // Fades in as it leaves the nozzle and out as it disperses, so a puff never
    // appears or vanishes on a frame.
    a *= smoothstep(0.0, 0.12, vLife) * smoothstep(1.0, 0.55, vLife);
    gl_FragColor = vec4(vCol, a * uDensity);
  }
`;

function flameVertex(world) {
  return `
  precision mediump float;
  attribute vec2 aQuad;         // -0.5..0.5 across, 0..1 up
  attribute vec3 aCol;          // (unused), height, seed
  attribute vec4 aBase;         // xy nozzle position, zw burn direction
  attribute float aAmp;         // 0..1 envelope
  uniform float uAspect;
  uniform vec3 uZoom;
  ${world ? WORLD_UNIFORMS : ''}
  varying vec2 vQ;
  varying float vSeed;
  varying float vAmp;
  void main() {
    vQ = vec2(aQuad.x, aQuad.y);
    vSeed = aCol.z; vAmp = aAmp;
    // The quad is as wide as the flame and as tall as it currently reaches, so
    // an envelope that grows and dies costs no extra fill at the ends.
    // Built from the nozzle's own frame: aBase.xy is where it sits, aBase.zw
    // the direction it burns in. The width runs across that direction, so a
    // column on a side wall lies flat instead of standing up in the wrong
    // place. Reduces exactly to the old floor-anchored form when the
    // direction is straight up.
    vec2 dir = normalize(aBase.zw);
    vec2 across = vec2(dir.y, -dir.x);
    vec2 p = aBase.xy + across * (aQuad.x * ${FLAME_WIDTH.toFixed(2)})
                      + dir * (aQuad.y * aCol.y);
${world ? `
    vec3 wp = vec3(uWorld.x + p.x * uWorld.w, uWorld.y + (p.y - ${GROUND_Y.toFixed(2)}) * uWorld.w, uWorld.z);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(wp, 1.0);
` : `
    p = uZoom.xy + (p - uZoom.xy) * uZoom.z;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
`}
  }
`;
}

const FLAME_FRAGMENT = `
  precision mediump float;
  uniform float uTime;
  uniform float uFlameGain;
  varying vec2 vQ;
  varying float vSeed;
  varying float vAmp;

${BLACKBODY_CHUNK}

  float hash12(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 3; i++) { v += a * vnoise(p); p *= 2.03; a *= 0.5; }
    return v;
  }

  void main() {
    float y = vQ.y, x = vQ.x * 2.0;
    // The noise SCROLLS DOWNWARD in the column's own frame, which is what makes
    // the flame look like it is being drawn upward rather than wobbling in
    // place. Everything else - the taper, the tear at the top - is a function
    // of height.
    float n = fbm(vec2(x * 2.6 + vSeed, y * 2.2 - uTime * 1.7 + vSeed));
    float n2 = fbm(vec2(x * 5.5 - vSeed, y * 4.4 - uTime * 2.6));

    // THE COLUMN SNAKES. A jet is not a straight cone: its axis wanders, and
    // the wander grows with height because the gas has had longer to be pushed
    // around. Without it the shape is symmetric and reads as drawn.
    float sway = (fbm(vec2(vSeed * 3.1, y * 1.5 - uTime * 1.1)) - 0.5) * 1.15 * y;
    x -= sway;

    // NARROW AT THE NOZZLE, widening as it rises. A jet flame leaves a small
    // orifice and spreads as it entrains air - the opposite of the taper a
    // candle has, and getting it the wrong way round put a solid white slab of
    // the column's full width and full temperature on the deck, which blew out
    // through the bloom and lit the whole stage.
    float mouth = smoothstep(0.0, 0.22, y);
    float w = mouth * (1.0 - y * 0.26) * (0.36 + 0.64 * n) * vAmp;
    float body = smoothstep(w, w * 0.30, abs(x));
    // The tip tears off into separate tongues instead of ending flat.
    body *= smoothstep(1.0, 0.34, y + (n2 - 0.5) * 1.05);
    if (body < 0.004) discard;

    // TEMPERATURE FALLS WITH HEIGHT, through the same curve the sparks use, so
    // the column and the gerbs read as the same fire rather than two effects.
    float t = clamp((1.02 - y * 0.90) * (0.52 + 0.80 * n2) * vAmp, 0.0, 1.12);
    vec3 col = blackbody(t);
    col += vec3(0.0, 0.05, 0.22) * smoothstep(0.92, 1.12, t);
    // 0.38, not 0.85. A column looks right at full strength against black, and
    // blows the frame out against everything else: it is a large additive quad,
    // the bloom threshold sees all of it, and the smoke in front of it scatters
    // the result across the whole stage. Judged in the composite, not alone.
    gl_FragColor = vec4(col * body * 0.30 * uFlameGain, body * 0.76);
  }
`;

// =============================================================================

export class PyroLayer {
  // options:
  //   max         particle cap (the ring buffer's size)
  //   trails, smoke, reflection, crackle, flames   feature switches
  //   linewidth   trail width in pixels
  //   gain        grains per charge, x (default 1)
  //   space       'clip' (default, Pyro's) or 'world'
  //   world       { x, y, z, scale, fog } - the plane, for space 'world'
  //   renderOrder an offset added to every object's order (default 0)
  constructor(options) {
    const o = options || {};
    this._max = o.max || REFERENCE_MAX;
    this._trailsOn = !!o.trails;
    this._smokeOn = !!o.smoke;
    this._reflectionOn = !!o.reflection;
    this._crackleOn = o.crackle !== false;
    this._flamesOn = !!o.flames;
    this._linewidth = o.linewidth != null ? o.linewidth : 1.7;
    this._zoom = { x: 0, y: GROUND_Y, f: 1 };
    this._gain = o.gain != null ? o.gain : 1;
    this._trailDim = 1;
    this._world = o.space === 'world';
    this._worldSpec = Object.assign({ x: 0, y: 0, z: 0, scale: 1, fog: 0 }, o.world || {});
    this._orderBase = o.renderOrder || 0;
    // THE DECK IS SOLID. In clip space the nozzle line is the frame's bottom
    // edge and a falling grain simply leaves the picture; on a stage it lands
    // on the deck, and a spark that has landed is out. Without this the last
    // second of every charge falls through the platform and turns up between
    // the silhouettes of the crowd as yellow patches - the one place fire
    // must never be. World-space consumers set it; Pyro's text is untouched.
    this._floorKill = !!o.floorKill;
    // How far above the nozzle line a puff is born, in simulation units. A
    // sprite is centred on its point, so a puff born ON the line hangs half
    // below it - below the lip of the stage, for a world-space consumer.
    this._smokeLift = o.smokeLift || 0;
    // The title extension (see TITLE_VERTEX_DECL): `title` is the most stars
    // a title may use; 0 leaves the layer exactly as it always was.
    this._titleMax = o.title || 0;
    this._shellSlots = o.shellSlots || 4;
    this._spawnSlot = 0;

    this.time = 0;
    this.flash = 0;
    this._flashThisFrame = 0;
    this._smokeDensity = 0;
    this._crackRate = 0;
    this._liveCount = 0;
  }

  get max() { return this._max; }
  get liveCount() { return this._liveCount; }
  get alive() { return this._alive; }
  get glow() { return this._glow; }
  get uniforms() { return this._shared; }
  get cometColor() { return this._shared.uCometCol.value; }
  get shellColor() { return this._shared.uShellCol.value; }
  get smokeColor() { return this._smokeUniforms ? this._smokeUniforms.uSmokeCol.value : null; }
  get crackle() { return this._crackleOn; }
  set crackle(v) { this._crackleOn = !!v; }

  init(scene) {
    this._scene = scene;
    const N = this._max;

    // ---- the particle store. ALL OF IT PRE-ALLOCATED, and a ring buffer
    // rather than a free list: a new spark overwrites the oldest one alive,
    // which is both allocation-free and self-limiting under a wall of fire.
    this._pos = new Float32Array(N * 2);
    this._prev = new Float32Array(N * 2);
    this._life = new Float32Array(N);
    this._invLife = new Float32Array(N);
    this._kind = new Float32Array(N);
    this._flare = new Float32Array(N);
    this._baseSize = new Float32Array(N);
    // PER-PARTICLE WEIGHT AND SHEDDING, and they are what make a willow a
    // willow rather than a slow peony. A willow star is a heavy composition
    // that falls harder than a gerb spark and sheds burning fragments the
    // whole way down; without both it is just a dim sphere that lasts longer.
    this._grav = new Float32Array(N);
    this._shed = new Float32Array(N);      // fragments per second
    this._shedAcc = new Float32Array(N);
    this._crackled = new Uint8Array(N);
    const title = this._titleMax > 0;
    if (title) {
      this._slot = new Float32Array(N);
      this._titleIdx = new Float32Array(N).fill(-1);
    }
    this._head = 0;
    this._alive = 0;

    // ---- the sparks: one THREE.Points for every particle on screen
    const geo = new THREE.BufferGeometry();
    this._aPos = new Float32Array(N * 3);
    this._aLife = new Float32Array(N);
    this._aSize = new Float32Array(N);
    this._aKind = new Float32Array(N);
    this._aFlare = new Float32Array(N);
    geo.setAttribute('position', new THREE.BufferAttribute(this._aPos, 3));
    geo.setAttribute('aLife', new THREE.BufferAttribute(this._aLife, 1));
    geo.setAttribute('aSize', new THREE.BufferAttribute(this._aSize, 1));
    geo.setAttribute('aKind', new THREE.BufferAttribute(this._aKind, 1));
    geo.setAttribute('aFlare', new THREE.BufferAttribute(this._aFlare, 1));
    if (title) {
      this._aSlot = new Float32Array(N);
      this._aTitle = new Float32Array(N).fill(-1);
      geo.setAttribute('aSlot', new THREE.BufferAttribute(this._aSlot, 1));
      geo.setAttribute('aTitle', new THREE.BufferAttribute(this._aTitle, 1));
    }
    geo.setDrawRange(0, 0);
    this._geo = geo;

    this._glow = [];
    for (let i = 0; i < GLOW_SLOTS; i++) this._glow.push(new THREE.Vector3(0, 0, 0));

    this._shared = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uPixel: { value: 3.0 },
      uCometCol: { value: new THREE.Color(0x7fd4ff) },
      uShellCol: { value: new THREE.Color(0xffb27f) },
      uPeak: { value: 0.96 },
      uMirror: { value: 0 },
      uZoom: { value: new THREE.Vector3(0, GROUND_Y, 1) }
    };
    if (this._world) {
      const w = this._worldSpec;
      this._shared.uWorld = { value: new THREE.Vector4(w.x, w.y, w.z, w.scale) };
      this._shared.uDepth = { value: new THREE.Vector2(1, w.fog || 0) };
    }
    if (title) {
      // the target texture: one RGBA float texel per star, square-ish
      const tw = Math.ceil(Math.sqrt(this._titleMax));
      const th = Math.ceil(this._titleMax / tw);
      this._titleData = new Float32Array(tw * th * 4);
      this._titleTex = new THREE.DataTexture(this._titleData, tw, th, THREE.RGBAFormat, THREE.FloatType);
      this._titleTex.minFilter = this._titleTex.magFilter = THREE.NearestFilter;
      this._titleTex.generateMipmaps = false;
      this._titleTex.needsUpdate = true;
      this._shared.uTitleTex = { value: this._titleTex };
      this._shared.uTitleTexSize = { value: new THREE.Vector2(tw, th) };
      this._shared.uTitle = { value: new THREE.Vector4(0, 0, 0, 0) };
      this._shared.uTitleGain = { value: 0.96 };
      this._shared.uPal = { value: [new THREE.Color(0xffffff), new THREE.Color(0xffffff), new THREE.Color(0xffffff), new THREE.Color(0xffffff)] };
      this._titleCount = 0;
      this._titleTargets = new Float32Array(this._titleMax * 2);
      this._titleState = { phase: 'idle', t: 0, hold: 0, rocket: { x: 0, y: 0, vy: 0, t: 0, dur: 0, emit: 0 }, bx: 0, by: 0, slot: 0 };
    }
    const world = this._world;
    const vertex = sparkVertex(world, title);
    // Points in world space are tested against the scene's depth, which is how
    // an opaque silhouette in front of the stage occludes them for free.
    const depthTest = world;
    // Where the objects sit in a scene's bounding volume. In clip space Three
    // never culls (frustumCulled false); in world space the plane is a known
    // box, and a sphere around it keeps the default culling honest.

    this._sparkMat = new THREE.ShaderMaterial({
      uniforms: this._shared,
      vertexShader: vertex,
      fragmentShader: sparkFragment(false, title),
      transparent: true, depthTest: depthTest, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._sparks = new THREE.Points(geo, this._sparkMat);
    this._sparks.frustumCulled = false;
    this._sparks.renderOrder = -2200 + this._orderBase;
    scene.add(this._sparks);

    // ---- the floor reflection: the same geometry, folded and dimmed.
    if (this._reflectionOn) {
      this._mirrorUniforms = Object.assign({}, this._shared, { uMirror: { value: 1 } });
      this._mirrorMat = new THREE.ShaderMaterial({
        uniforms: this._mirrorUniforms,
        vertexShader: vertex,
        fragmentShader: sparkFragment(true, title),
        transparent: true, depthTest: depthTest, depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      this._mirror = new THREE.Points(geo, this._mirrorMat);
      this._mirror.frustumCulled = false;
      this._mirror.renderOrder = -2500 + this._orderBase;
      scene.add(this._mirror);
    }

    // ---- the trails. A spark drawn as a point looks suspended; drawn as a
    // segment from where it was two frames ago to where it is now, it has a
    // direction and a speed. The previous position is kept anyway for Verlet,
    // so the trail costs a draw call and no extra state.
    if (this._trailsOn) {
      this._trailPos = new Float32Array(N * 6);
      const tgeo = new LineSegmentsGeometry();
      tgeo.setPositions(this._trailPos);
      this._trailCol = new Float32Array(N * 6);
      tgeo.setColors(this._trailCol);
      this._trailGeo = tgeo;
      this._trailStart = tgeo.attributes.instanceStart.data;   // interleaved, mutated in place
      this._trailColStart = tgeo.attributes.instanceColorStart.data;
      this._trailMat = new LineMaterial({
        linewidth: this._linewidth,
        vertexColors: true,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthTest: depthTest, depthWrite: false
      });
      const win = typeof window !== 'undefined' ? window : {};
      this._trailMat.resolution.set(win.innerWidth || 1280, win.innerHeight || 720);
      this._trails = new LineSegments2(tgeo, this._trailMat);
      this._trails.frustumCulled = false;
      this._trails.renderOrder = -2300 + this._orderBase;
      tgeo.instanceCount = 0;
      scene.add(this._trails);
    }

    // ---- smoke
    if (this._smokeOn) {
      this._smokePos = new Float32Array(SMOKE_MAX * 2);
      this._smokeVel = new Float32Array(SMOKE_MAX * 2);
      this._smokeLife = new Float32Array(SMOKE_MAX);
      this._smokeInv = new Float32Array(SMOKE_MAX);
      this._smokeSize = new Float32Array(SMOKE_MAX);
      this._smokeHead = 0;
      this._smokeAlive = 0;
      const sgeo = new THREE.BufferGeometry();
      this._sPos = new Float32Array(SMOKE_MAX * 3);
      this._sLife = new Float32Array(SMOKE_MAX);
      this._sSize = new Float32Array(SMOKE_MAX);
      sgeo.setAttribute('position', new THREE.BufferAttribute(this._sPos, 3));
      sgeo.setAttribute('aLife', new THREE.BufferAttribute(this._sLife, 1));
      sgeo.setAttribute('aSize', new THREE.BufferAttribute(this._sSize, 1));
      sgeo.setDrawRange(0, 0);
      this._smokeGeo = sgeo;
      this._smokeUniforms = {
        uAspect: safeZoneUniforms.uAspect,
        uPixel: this._shared.uPixel,
        uSmokeCol: { value: new THREE.Color(0x6f86a8) },
        uZoom: this._shared.uZoom,
        uGlowPos: { value: this._glow },
        uDensity: { value: 0 }
      };
      if (world) {
        this._smokeUniforms.uWorld = this._shared.uWorld;
        this._smokeUniforms.uDepth = this._shared.uDepth;
        this._lightA = []; this._lightD = []; this._lightC = [];
        for (let i = 0; i < WORLD_LIGHTS; i++) {
          this._lightA.push(new THREE.Vector3(0, -999, 0));
          this._lightD.push(new THREE.Vector3(0, -1, 0));
          this._lightC.push(new THREE.Color(0, 0, 0));
        }
        this._smokeUniforms.uLightA = { value: this._lightA };
        this._smokeUniforms.uLightD = { value: this._lightD };
        this._smokeUniforms.uLightC = { value: this._lightC };
      }
      this._smokeMat = new THREE.ShaderMaterial({
        uniforms: this._smokeUniforms,
        vertexShader: smokeVertex(world),
        fragmentShader: SMOKE_FRAGMENT,
        transparent: true, depthTest: depthTest, depthWrite: false,
        blending: THREE.NormalBlending
      });
      this._smoke = new THREE.Points(sgeo, this._smokeMat);
      this._smoke.frustumCulled = false;
      this._smoke.renderOrder = -2400 + this._orderBase;
      scene.add(this._smoke);
    }

    // ---- flame columns: one instanced quad, FLAME_SLOTS of them at most.
    if (this._flamesOn) {
      const q = new Float32Array([-0.5, 0, 0.5, 0, -0.5, 1, 0.5, 1]);
      const fgeo = new THREE.InstancedBufferGeometry();
      fgeo.setAttribute('aQuad', new THREE.BufferAttribute(q, 2));
      fgeo.setIndex([0, 1, 2, 2, 1, 3]);
      this._flameCol = new Float32Array(FLAME_SLOTS * 3);
      this._flameAmp = new Float32Array(FLAME_SLOTS);
      this._flameBase = new Float32Array(FLAME_SLOTS * 4);
      fgeo.setAttribute('aCol', new THREE.InstancedBufferAttribute(this._flameCol, 3));
      fgeo.setAttribute('aAmp', new THREE.InstancedBufferAttribute(this._flameAmp, 1));
      fgeo.setAttribute('aBase', new THREE.InstancedBufferAttribute(this._flameBase, 4));
      fgeo.instanceCount = 0;
      this._flameGeo = fgeo;
      this._flameUniforms = { uAspect: safeZoneUniforms.uAspect, uTime: { value: 0 }, uZoom: this._shared.uZoom, uFlameGain: { value: 1 } };
      if (world) {
        this._flameUniforms.uWorld = this._shared.uWorld;
        this._flameUniforms.uDepth = this._shared.uDepth;
      }
      this._flameMat = new THREE.ShaderMaterial({
        uniforms: this._flameUniforms,
        vertexShader: flameVertex(world),
        fragmentShader: FLAME_FRAGMENT,
        transparent: true, depthTest: depthTest, depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      this._flames = new THREE.Mesh(fgeo, this._flameMat);
      this._flames.frustumCulled = false;
      // Behind the sparks, in front of the smoke: a gerb fired out of a burning
      // column has to pass in front of it.
      this._flames.renderOrder = -2350 + this._orderBase;
      scene.add(this._flames);
      this._flameState = [];
      for (let i = 0; i < FLAME_SLOTS; i++) {
        this._flameState.push({ live: false, x: 0, y: GROUND_Y, dx: 0, dy: 1, t: 0, dur: 0, h: 0, seed: 0 });
      }
    }

    // ---- shells in flight, pre-allocated
    this._shells = [];
    for (let i = 0; i < this._shellSlots; i++) {
      this._shells.push({ live: false, x: 0, y: 0, vx: 0, vy: 0, t: 0, dur: 0,
                          apex: 0, emit: 0, shape: 0, slot: 0 });
    }

    // ---- comets in flight, pre-allocated
    this._comets = [];
    for (let i = 0; i < 6; i++) {
      this._comets.push({ live: false, x: 0, y: 0, vx: 0, vy: 0, t: 0, dur: 0, emit: 0, slot: 0 });
    }
    // ---- gerbs in flight (emitters), pre-allocated
    this._emitters = [];
    for (let i = 0; i < 24; i++) {
      this._emitters.push({ live: false, x: 0, y: 0, t: 0, dur: 0, left: 0, total: 0, vlo: 0, vhi: 0, tilt: 0, kind: 0 });
    }
  }

  // --- configuration ---------------------------------------------------------

  // Point size is in device pixels, so it has to follow the real buffer or a
  // spark is three pixels on a desktop and one on the kiosk. About 1.6 device
  // pixels for a base-size grain on a 720-line buffer. Pyro sparks are points
  // of light, not sprites; anything over three or four pixels stops reading
  // as burning metal.
  // pixel: an explicit uPixel for a consumer that has done its own arithmetic
  // (a world-space layer, where a simulation unit is not half the buffer).
  setResolution(width, height, pixel) {
    this._shared.uPixel.value = pixel != null ? pixel : Math.max(1.0, height / 420);
    if (this._trailMat) this._trailMat.resolution.set(width, height);
  }

  // Clip mode: bring the view closer by `factor` about (px, py). Everything
  // drawn grows - trajectories, flames, smoke, grains, trail width.
  setZoom(factor, px, py) {
    const z = this._zoom;
    z.f = factor; if (px != null) z.x = px; if (py != null) z.y = py;
    this._shared.uZoom.value.set(z.x, z.y, z.f);
    // The trails are NOT widened: they already lengthen by the zoom, and they
    // carry most of the frame's light (measured: mean 62 on 255 with them, 20
    // without). Widened too they nearly tripled it, and the bloom made white
    // blobs of them. Their colour is divided by the zoom instead (below).
    this._trailDim = 1 / z.f;
    // A flame zoom times bigger is zoom^2 the lit area through the bloom.
    if (this._flameUniforms) this._flameUniforms.uFlameGain.value = Math.pow(z.f, FLAME_ZOOM_DIM);
  }
  // Screen (clip view) to simulation coordinates and back, under the zoom.
  fromScreen(x, y) { const z = this._zoom; return [z.x + (x - z.x) / z.f, z.y + (y - z.y) / z.f]; }
  toScreen(x, y) { const z = this._zoom; return [z.x + (x - z.x) * z.f, z.y + (y - z.y) * z.f]; }

  // World mode only: where the plane sits, how big a simulation unit is, and
  // the view depth the point size was computed for.
  setWorld(x, y, z, scale, refDepth) {
    if (!this._world) return;
    const w = this._worldSpec;
    w.x = x; w.y = y; w.z = z; w.scale = scale;
    this._shared.uWorld.value.set(x, y, z, scale);
    if (refDepth != null) this._shared.uDepth.value.x = refDepth;
  }

  // World mode, smoke only: an external beam the puffs are lit by.
  setLight(i, origin, direction, color, gain) {
    if (!this._lightA || i < 0 || i >= WORLD_LIGHTS) return;
    this._lightA[i].copy(origin);
    this._lightD[i].copy(direction).normalize();
    this._lightC[i].copy(color).multiplyScalar(gain != null ? gain : 1);
  }

  // --- particle emission --------------------------------------------------

  spawn(x, y, vx, vy, life, size, kind, flare, grav, shed) {
    const i = this._head;
    this._head = (this._head + 1) % this._max;
    if (this._alive < this._max) this._alive++;
    const i2 = i * 2;
    this._pos[i2] = x; this._pos[i2 + 1] = y;
    // Verlet: the previous position IS the velocity. Storing it this way means
    // the integrator and the trail read the same two numbers.
    this._prev[i2] = x - vx * 0.0166667;
    this._prev[i2 + 1] = y - vy * 0.0166667;
    this._life[i] = life;
    this._invLife[i] = 1 / life;
    this._kind[i] = kind;
    this._flare[i] = flare;
    this._baseSize[i] = size;
    this._grav[i] = grav != null ? grav : 1;
    this._shed[i] = shed != null ? shed : 0;
    this._shedAcc[i] = 0;
    this._crackled[i] = 0;
    if (this._slot) { this._slot[i] = this._spawnSlot; this._titleIdx[i] = -1; }
    return i;
  }

  fireGerb(n, vlo, vhi, count, tilt, kind) {
    for (let e = 0; e < this._emitters.length; e++) {
      const m = this._emitters[e];
      if (m.live) continue;
      m.live = true; m.x = n.x; m.y = n.y; m.t = 0; m.dur = EMIT_MS / 1000;
      m.left = count; m.total = count; m.vlo = vlo; m.vhi = vhi; m.kind = kind;
      // tilt is relative to the nozzle's barrel, so a cone aimed "slightly
      // outward" means the same thing on the ceiling as on the floor.
      m.tilt = n.tilt + tilt;
      return;
    }
  }

  fireComet(n) {
    for (let c = 0; c < this._comets.length; c++) {
      const m = this._comets[c];
      if (m.live) continue;
      m.live = true; m.x = n.x; m.y = n.y;
      m.dur = COMET_RISE[0] + Math.random() * (COMET_RISE[1] - COMET_RISE[0]);
      // Constant speed, so it reads as driven rather than thrown - a comet is
      // a rocket, not a spark. Along the nozzle's own barrel now, so one
      // fired from the ceiling drives DOWN at the same speed.
      const v = (0.55 + Math.random() * 0.55) / m.dur + 0.85;
      m.vx = n.dx * v; m.vy = n.dy * v;
      m.t = 0; m.emit = 0; m.slot = this._spawnSlot;
      return;
    }
  }

  fireShell(n, shape, apex) {
    for (let i = 0; i < this._shells.length; i++) {
      const m = this._shells[i];
      if (m.live) continue;
      m.live = true; m.x = n.x; m.y = n.y; m.t = 0; m.emit = 0;
      m.shape = shape != null ? shape : (Math.random() * SHELL_SHAPES.length) | 0;
      m.dur = SHELL_RISE[0] + Math.random() * (SHELL_RISE[1] - SHELL_RISE[0]);
      m.apex = apex != null ? apex : SHELL_APEX[0] + Math.random() * (SHELL_APEX[1] - SHELL_APEX[0]);
      m.slot = this._spawnSlot;
      // Constant travel to a chosen DISTANCE down the barrel, so the break
      // still lands where it was aimed. Measured from the nozzle rather than
      // from the floor, which is what lets a shell fired from the ceiling or
      // a side wall break at a sensible place instead of off-screen.
      const reach = Math.abs(m.apex - GROUND_Y);
      m.vx = n.dx * reach / m.dur;
      m.vy = n.dy * reach / m.dur;
      return;
    }
  }

  _burstShell(m) {
    const name = SHELL_SHAPES[m.shape];
    const cfg = SHELL_STARS[name];
    const n = Math.round(cfg.n * this.scale());
    // Every shape is the SAME radial burst with the aim and the burn changed.
    // That is also how they differ in reality: one charge, different stars.
    for (let i = 0; i < n; i++) {
      let a, v;
      if (name === 'anneau') {
        // A ring only stays a ring if the speed spread is tiny - a couple of
        // percent. Widen it and the circle fills in and becomes a peony.
        a = (i / n) * Math.PI * 2;
        v = cfg.v[0] + Math.random() * (cfg.v[1] - cfg.v[0]);
      } else if (name === 'palmier') {
        // A few thick fronds instead of a uniform sphere.
        const frond = (i % PALM_FRONDS) / PALM_FRONDS;
        a = frond * Math.PI * 2 + (Math.random() - 0.5) * 0.22;
        v = cfg.v[0] + Math.random() * (cfg.v[1] - cfg.v[0]);
      } else {
        a = Math.random() * Math.PI * 2;
        v = cfg.v[0] + Math.random() * (cfg.v[1] - cfg.v[0]);
      }
      const life = cfg.life[0] + Math.random() * (cfg.life[1] - cfg.life[0]);
      this.spawn(m.x, m.y, Math.cos(a) * v, Math.sin(a) * v, life,
                 1.70 + Math.random() * 1.30, 2, 0, cfg.droop, cfg.trail);
    }
    m.droop = cfg.droop;
    this._flashThisFrame = Math.max(this._flashThisFrame, 0.35);
  }

  fireFlame(n) {
    if (!this._flamesOn) return;
    for (let i = 0; i < this._flameState.length; i++) {
      const m = this._flameState[i];
      if (m.live) continue;
      m.live = true; m.x = n.x; m.y = n.y; m.dx = n.dx; m.dy = n.dy; m.t = 0;
      m.dur = FLAME_LIFE[0] + Math.random() * (FLAME_LIFE[1] - FLAME_LIFE[0]);
      m.h = FLAME_HEIGHT[0] + Math.random() * (FLAME_HEIGHT[1] - FLAME_HEIGHT[0]);
      m.seed = Math.random() * 40;
      return;
    }
  }

  // The wall: a curtain drawn right across a line of nozzles, each one fired
  // straight up and leaning away from the centre. The nozzles are the
  // caller's - Pyro walks its own evenly spaced line across the floor,
  // MainStage fires its six.
  fireWall(nozzles, vlo, vhi, perNozzle) {
    for (let i = 0; i < nozzles.length; i++) {
      const n = nozzles[i];
      this.fireGerb(n, vlo, vhi, perNozzle, n.x < 0 ? -NOZZLE_TILT : NOZZLE_TILT, 0);
    }
    this._wallUntil = this.time + WALL_MS / 1000;
    this._flashThisFrame = 1;
  }

  emitSmoke(x) {
    if (!this._smokeOn) return;
    const i = this._smokeHead;
    this._smokeHead = (this._smokeHead + 1) % SMOKE_MAX;
    if (this._smokeAlive < SMOKE_MAX) this._smokeAlive++;
    const i2 = i * 2;
    this._smokePos[i2] = x + (Math.random() - 0.5) * 0.06;
    this._smokePos[i2 + 1] = GROUND_Y + 0.02 + this._smokeLift;
    this._smokeVel[i2] = (Math.random() - 0.5) * 0.045;
    this._smokeVel[i2 + 1] = SMOKE_RISE * (0.7 + Math.random() * 0.6);
    const L = SMOKE_LIFE[0] + Math.random() * (SMOKE_LIFE[1] - SMOKE_LIFE[0]);
    this._smokeLife[i] = L;
    this._smokeInv[i] = 1 / L;
    this._smokeSize[i] = 18 + Math.random() * 22;
  }

  // gain: grains per charge on top of the budget ratio (Pyro's CLOSER view,
  // 1.0 for everyone else).
  scale() { return (this._max / REFERENCE_MAX) * this._gain; }

  // --- the frame ----------------------------------------------------------

  // Everything that was fired since the last call is run forward by dt. Call
  // order is Pyro's exactly, which is what keeps the two identical.
  // high: the director's high band (drives the crackle rate). progress: the
  // track's, loads the air up with haze over its length.
  update(dt, high, progress) {
    this.time += dt;
    this._runEmitters(dt, high);
    this._runComets(dt);
    this._runShells(dt);
    if (this._titleState) this._runTitle(dt, high);
    this._runFlames(dt);
    this._integrate(dt, high);
    this._runSmoke(dt, progress || 0);
    this._writeGlows();
    this.flash = this._flashThisFrame;
    this._flashThisFrame = 0;
    return this.flash;
  }

  _runShells(dt) {
    for (let i = 0; i < this._shells.length; i++) {
      const m = this._shells[i];
      if (!m.live) continue;
      if (this._slot) this._spawnSlot = m.slot;
      m.t += dt;
      m.x += (m.vx || 0) * dt;
      m.y += m.vy * dt;
      // the tail on the way up
      m.emit += SHELL_TRAIL_PER_S * dt;
      while (m.emit >= 1) {
        m.emit -= 1;
        this.spawn(m.x + (Math.random() - 0.5) * 0.012, m.y,
                   (Math.random() - 0.5) * 0.25, -0.1 - Math.random() * 0.35,
                   0.30 + Math.random() * 0.5, 0.95 + Math.random() * 0.5, 0, 0);
      }
      this.spawn(m.x, m.y, 0, 0, 0.022, 2.0, 0, 0.30);
      if (m.t >= m.dur) { m.live = false; this._burstShell(m); }
    }
  }

  _runFlames(dt) {
    if (!this._flamesOn) return;
    let w = 0;
    for (let i = 0; i < this._flameState.length; i++) {
      const m = this._flameState[i];
      if (!m.live) continue;
      m.t += dt;
      if (m.t >= m.dur) { m.live = false; continue; }
      const u = m.t / m.dur;
      // Up fast, hold, die back: a gas column does not fade linearly.
      const amp = Math.min(1, u * 6.5) * (1 - Math.pow(Math.max(0, (u - 0.45) / 0.55), 1.8));
      this._flameCol[w * 3] = m.x;
      this._flameCol[w * 3 + 1] = m.h * (0.45 + 0.55 * amp);
      this._flameCol[w * 3 + 2] = m.seed;
      this._flameBase[w * 4] = m.x;
      this._flameBase[w * 4 + 1] = m.y;
      this._flameBase[w * 4 + 2] = m.dx;
      this._flameBase[w * 4 + 3] = m.dy;
      this._flameAmp[w] = amp;
      w++;
    }
    this._flameGeo.instanceCount = w;
    if (w > 0) {
      this._flameGeo.attributes.aCol.needsUpdate = true;
      this._flameGeo.attributes.aAmp.needsUpdate = true;
      this._flameGeo.attributes.aBase.needsUpdate = true;
    }
    this._flameUniforms.uTime.value = this.time;
  }

  // Emitters run down over EMIT_MS with a FALLING rate: a gerb is loudest at
  // the moment the charge lights and thins out after, and a constant rate looks
  // like a hose.
  _runEmitters(dt, high) {
    const crackRate = this._crackleOn ? CRACKLE_CHANCE * (0.6 + 0.9 * high) : 0;
    this._crackRate = crackRate;
    for (let e = 0; e < this._emitters.length; e++) {
      const m = this._emitters[e];
      if (!m.live) continue;
      const before = m.t / m.dur;
      m.t += dt;
      const after = Math.min(1, m.t / m.dur);
      // integral of a falling rate, normalised: 1 - (1-u)^2
      const wantBefore = (1 - (1 - Math.min(1, before)) * (1 - Math.min(1, before))) * m.total;
      const wantAfter = (1 - (1 - after) * (1 - after)) * m.total;
      let n = Math.round(wantAfter - wantBefore);
      while (n-- > 0) {
        const a = m.tilt + (Math.random() - 0.5) * 2 * (CONE_DEG * Math.PI / 180);
        const v = m.vlo + Math.random() * (m.vhi - m.vlo);
        const life = LIFE_MIN + Math.random() * (LIFE_MAX - LIFE_MIN);
        this.spawn(m.x + (Math.random() - 0.5) * 0.012, m.y,
                   Math.sin(a) * v, Math.cos(a) * v, life,
                   1.45 + Math.random() * 1.25, m.kind, 0);
      }
      if (after >= 1) m.live = false;
    }
  }

  _runComets(dt) {
    for (let c = 0; c < this._comets.length; c++) {
      const m = this._comets[c];
      if (!m.live) continue;
      if (this._slot) this._spawnSlot = m.slot;
      m.t += dt;
      m.x += (m.vx || 0) * dt;
      m.y += m.vy * dt;
      // the dense falling trail behind the core
      m.emit += COMET_TRAIL_PER_S * dt;
      while (m.emit >= 1) {
        m.emit -= 1;
        this.spawn(m.x + (Math.random() - 0.5) * 0.02, m.y,
                   (Math.random() - 0.5) * 0.35, -0.15 - Math.random() * 0.5,
                   0.45 + Math.random() * 0.7, 0.95 + Math.random() * 0.6, 1, 0);
      }
      // the core itself, drawn fat and bright
      // The core, re-stamped each frame so it tracks the comet. Its life is
      // barely longer than a frame ON PURPOSE: at 0.09 s five of them overlap
      // at once and the head blows out into a white disc the size of a gerb.
      this.spawn(m.x, m.y, 0, 0, 0.022, 2.3, 1, 0.35);
      if (m.t >= m.dur) {
        m.live = false;
        // APOGEE: a radial burst with the same physics and the same crackle as
        // a gerb, which is what keeps it in the same world as the rest.
        const n = Math.round(COMET_BURST * this.scale());
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const v = COMET_BURST_V[0] + Math.random() * (COMET_BURST_V[1] - COMET_BURST_V[0]);
          this.spawn(m.x, m.y, Math.cos(a) * v, Math.sin(a) * v,
                     LIFE_MIN * 0.8 + Math.random() * 1.4, 1.55 + Math.random() * 1.15, 1, 0);
        }
      }
    }
  }

  // Verlet, with quadratic drag. Two substeps at most, which is enough at these
  // speeds and keeps the cost bounded when a frame runs long.
  _integrate(dt, high) {
    const steps = dt > 0.022 ? 2 : 1;
    const h = dt / steps;
    const n = this._alive;
    const pos = this._pos, prev = this._prev, life = this._life, grav = this._grav;
    for (let s = 0; s < steps; s++) {
      const h2 = h * h;
      for (let i = 0; i < n; i++) {
        if (life[i] <= 0) continue;
        const i2 = i * 2;
        const x = pos[i2], y = pos[i2 + 1];
        const vx = (x - prev[i2]) / h, vy = (y - prev[i2 + 1]) / h;
        const sp = Math.sqrt(vx * vx + vy * vy);
        // Drag grows with speed, so a spark is stopped hard at first and drifts
        // afterwards. This is the term that makes it a gerb.
        const d = DRAG * sp;
        const ax = -vx * d;
        const ay = -vy * d - G * grav[i];
        prev[i2] = x; prev[i2 + 1] = y;
        pos[i2] = x + vx * h + ax * h2;
        pos[i2 + 1] = y + vy * h + ay * h2;
      }
    }

    // life, crackle and the GPU attributes, in one pass
    const aPos = this._aPos, aLife = this._aLife, aSize = this._aSize,
          aKind = this._aKind, aFlare = this._aFlare;
    const inv = this._invLife, kind = this._kind, flare = this._flare,
          base = this._baseSize, crack = this._crackled, shed = this._shed;
    const crackRate = this._crackRate || 0;
    let write = 0;
    const trailPos = this._trailPos, trailCol = this._trailCol;
    let tw = 0;
    const floorKill = this._floorKill;
    // World mode: the trail segments go through the camera like any mesh, so
    // they are written in world units. The sparks map themselves in the
    // shader; the trails are the one buffer mapped here.
    const world = this._world;
    const wx = this._worldSpec.x, wy = this._worldSpec.y, wz = this._worldSpec.z,
          ws = this._worldSpec.scale;
    for (let i = 0; i < n; i++) {
      if (life[i] <= 0) continue;
      life[i] -= dt;
      if (life[i] <= 0) continue;
      const u = life[i] * inv[i];              // 1 at birth, 0 at burnout
      const i2 = i * 2;
      if (floorKill && pos[i2 + 1] < GROUND_Y) { life[i] = 0; continue; }

      // A willow or a palm star burning its way down, dropping fragments.
      // Kept to shell stars: this is the composition burning, not a spark
      // breaking up, and the two look different because one leaves a
      // continuous thread and the other a sudden flash.
      if (this._slot) this._spawnSlot = this._slot[i];
      if (shed[i] > 0) {
        this._shedAcc[i] += shed[i] * dt;
        while (this._shedAcc[i] >= 1) {
          this._shedAcc[i] -= 1;
          this.spawn(pos[i2], pos[i2 + 1],
                     (Math.random() - 0.5) * 0.18, -0.05 - Math.random() * 0.25,
                     0.22 + Math.random() * 0.34, base[i] * 0.72, kind[i], 0, 1.0, 0);
        }
      }

      if (crackRate > 0 && !crack[i] && kind[i] < 0.5 && u < 1 - CRACKLE_AT) {
        crack[i] = 1;
        if (Math.random() < crackRate) {
          for (let c = 0; c < CRACKLE_CHILDREN; c++) {
            const a = Math.random() * Math.PI * 2;
            const v = CRACKLE_SPEED * (0.4 + Math.random());
            this.spawn(pos[i2], pos[i2 + 1], Math.cos(a) * v, Math.sin(a) * v,
                       CRACKLE_LIFE[0] + Math.random() * (CRACKLE_LIFE[1] - CRACKLE_LIFE[0]),
                       base[i] * 1.25, kind[i], CRACKLE_FLARE, 1.0, 0);
          }
        }
      }

      const w = write * 3;
      aPos[w] = pos[i2]; aPos[w + 1] = pos[i2 + 1]; aPos[w + 2] = 0;
      aLife[write] = u;
      aSize[write] = base[i] * (0.55 + 0.75 * u);
      aKind[write] = kind[i];
      aFlare[write] = flare[i] * u;
      if (this._slot) {
        this._aSlot[write] = this._slot[i];
        const ti = this._titleIdx[i];
        this._aTitle[write] = ti;
        // a star of the lettering keeps its full size while it writes
        if (ti >= 0 && this._titleState.phase !== 'fall') aSize[write] = base[i] * 1.25;
      }
      write++;
      // A title star has no trail: the shader draws it on its letter, while
      // the position the trail would follow is the ballistic one underneath.
      if (this._titleIdx && this._titleIdx[i] >= 0) continue;

      if (trailPos) {
        // The segment is where it was against where it is. Its length is its
        // speed, which is the whole point: short near the apex, long at the
        // nozzle and on the way down.
        const t6 = tw * 6;
        // Two frames back, as the brief asks, from the one frame Verlet keeps:
        // the last displacement doubled. A one-frame streak is barely longer
        // than the grain itself and the trail does not read at all.
        const x0 = pos[i2] - 2.0 * (pos[i2] - prev[i2]);
        const y0 = pos[i2 + 1] - 2.0 * (pos[i2 + 1] - prev[i2 + 1]);
        if (world) {
          trailPos[t6] = wx + x0 * ws;
          trailPos[t6 + 1] = wy + (y0 - GROUND_Y) * ws;
          trailPos[t6 + 2] = wz;
          trailPos[t6 + 3] = wx + pos[i2] * ws;
          trailPos[t6 + 4] = wy + (pos[i2 + 1] - GROUND_Y) * ws;
          trailPos[t6 + 5] = wz;
        } else {
          // The clip view's zoom, here on the CPU: LineMaterial has its own
          // vertex shader.
          const z = this._zoom;
          trailPos[t6] = z.x + (x0 - z.x) * z.f;
          trailPos[t6 + 1] = z.y + (y0 - z.y) * z.f;
          trailPos[t6 + 2] = 0;
          trailPos[t6 + 3] = z.x + (pos[i2] - z.x) * z.f;
          trailPos[t6 + 4] = z.y + (pos[i2 + 1] - z.y) * z.f;
          trailPos[t6 + 5] = 0;
        }
        const t = Math.pow(u, 0.55);
        const cr = Math.min(1, t * 3.2), cg = Math.min(1, Math.max(0, (t - 0.22) * 2.1)),
              cb = Math.min(1, Math.max(0, (t - 0.58) * 2.4));
        const k = kind[i];
        const cc = this._shared.uCometCol.value;
        const td = this._trailDim;
        trailCol[t6] = (cr * (1 - k) + cc.r * t * k) * td;
        trailCol[t6 + 1] = (cg * (1 - k) + cc.g * t * k) * td;
        trailCol[t6 + 2] = (cb * (1 - k) + cc.b * t * k) * td;
        trailCol[t6 + 3] = trailCol[t6] * 0.25;
        trailCol[t6 + 4] = trailCol[t6 + 1] * 0.25;
        trailCol[t6 + 5] = trailCol[t6 + 2] * 0.25;
        tw++;
      }
    }

    this._geo.setDrawRange(0, write);
    this._geo.attributes.position.needsUpdate = true;
    this._geo.attributes.aLife.needsUpdate = true;
    this._geo.attributes.aSize.needsUpdate = true;
    this._geo.attributes.aKind.needsUpdate = true;
    this._geo.attributes.aFlare.needsUpdate = true;
    if (this._slot) {
      this._geo.attributes.aSlot.needsUpdate = true;
      this._geo.attributes.aTitle.needsUpdate = true;
    }
    this._liveCount = write;

    if (this._trailGeo) {
      this._trailGeo.instanceCount = tw;
      this._trailStart.needsUpdate = true;
      this._trailColStart.needsUpdate = true;
    }
  }

  _runSmoke(dt, progress) {
    if (!this._smokeOn) return;
    const n = this._smokeAlive, pos = this._smokePos, vel = this._smokeVel,
          life = this._smokeLife, inv = this._smokeInv, size = this._smokeSize;
    const sPos = this._sPos, sLife = this._sLife, sSize = this._sSize;
    let write = 0;
    for (let i = 0; i < n; i++) {
      if (life[i] <= 0) continue;
      life[i] -= dt;
      if (life[i] <= 0) continue;
      const i2 = i * 2;
      pos[i2] += vel[i2] * dt;
      pos[i2 + 1] += vel[i2 + 1] * dt;
      vel[i2] *= 1 - dt * 0.35;
      const u = life[i] * inv[i];
      const w = write * 3;
      sPos[w] = pos[i2]; sPos[w + 1] = pos[i2 + 1]; sPos[w + 2] = 0;
      sLife[write] = u;
      // A puff spreads as it rises, which is what turns a dot into smoke.
      sSize[write] = size[i] * (1.0 + (1 - u) * 1.9);
      write++;
    }
    this._smokeGeo.setDrawRange(0, write);
    this._smokeGeo.attributes.position.needsUpdate = true;
    this._smokeGeo.attributes.aLife.needsUpdate = true;
    this._smokeGeo.attributes.aSize.needsUpdate = true;
    // THE AIR LOADS UP over the track: after a few minutes there is real haze,
    // which is what makes the later gerbs better than the early ones. Capped,
    // or the last third of a track is fog.
    const target = Math.min(SMOKE_CAP, 0.10 + clamp01(progress) * 1.2);
    this._smokeDensity += (target - this._smokeDensity) * Math.min(1, dt * 0.25);
    // Puffs zoom times wider overlap zoom times more: thinner, same haze.
    this._smokeUniforms.uDensity.value = this._smokeDensity / this._zoom.f;
  }

  // The ambient glows: one slot per emitter still burning, brightest at the
  // moment of ignition. Written into a pre-allocated Vector3 array that both
  // the background and the smoke read.
  _writeGlows() {
    let s = 0;
    for (let e = 0; e < this._emitters.length && s < GLOW_SLOTS; e++) {
      const m = this._emitters[e];
      if (!m.live) continue;
      const u = 1 - Math.min(1, m.t / m.dur);
      this._glow[s].set(m.x, GROUND_Y + 0.05, u * u * 1.15);
      s++;
    }
    for (let c = 0; c < this._comets.length && s < GLOW_SLOTS; c++) {
      const m = this._comets[c];
      if (!m.live) continue;
      this._glow[s].set(m.x, m.y, 0.30);
      s++;
    }
    for (; s < GLOW_SLOTS; s++) this._glow[s].z = 0;
  }

  // --- the title extension (option `title`) ---------------------------------

  // The colour slot the next fired thing (comet, shell, title) burns in.
  setSlot(i) { this._spawnSlot = i; }
  setPalette(colors) {
    if (!this._shared.uPal) return;
    for (let i = 0; i < 4; i++) this._shared.uPal.value[i].copy(colors[i % colors.length]);
  }
  // The whole layer's light, title excepted: Shapes dims its fireworks under
  // a shape at full intensity. Trails take it through their opacity.
  setIntensity(g, titleGain) {
    this._shared.uPeak.value = 0.96 * g;
    if (this._trailMat) this._trailMat.opacity = g;
    if (this._shared.uTitleGain && titleGain != null) this._shared.uTitleGain.value = 0.96 * titleGain;
  }
  // xy: the title's stars, in simulation units, count of them. ONE upload,
  // at the track change - never per frame.
  setTitleTargets(xy, count) {
    if (!this._titleTex) return;
    const n = Math.min(count, this._titleMax);
    this._titleData.fill(0);
    for (let i = 0; i < n; i++) {
      this._titleData[i * 4] = xy[i * 2];
      this._titleData[i * 4 + 1] = xy[i * 2 + 1];
      this._titleTargets[i * 2] = xy[i * 2];
      this._titleTargets[i * 2 + 1] = xy[i * 2 + 1];
    }
    this._titleTex.needsUpdate = true;
    this._titleCount = n;
  }
  get titleBusy() { return !!this._titleState && this._titleState.phase !== 'idle'; }
  get titlePhase() { return this._titleState ? this._titleState.phase : 'idle'; }
  // The title bouquet: a rocket from (x, GROUND_Y) breaking at (bx, by), a
  // spherical burst for TITLE_FREE seconds, pulled onto the lettering by
  // TITLE_FORMED, held for `hold` seconds, then let go to fall for
  // TITLE_FALL seconds.
  fireTitle(x, bx, by, hold) {
    const st = this._titleState;
    if (!st || this._titleCount === 0 || st.phase !== 'idle') return false;
    const r = st.rocket;
    r.x = x; r.y = GROUND_Y; r.t = 0; r.emit = 0;
    r.dur = 1.1; r.vx = (bx - x) / r.dur; r.vy = (by - GROUND_Y) / r.dur;
    st.bx = bx; st.by = by; st.hold = hold; st.t = 0; st.phase = 'rise';
    st.slot = this._spawnSlot;
    return true;
  }
  _runTitle(dt, high) {
    const st = this._titleState;
    const U = this._shared.uTitle.value;
    U.y = this.time; U.z = 0.35 + 0.65 * (high || 0);
    if (st.phase === 'idle') { U.x = 0; U.w = 0; return; }
    this._spawnSlot = st.slot;
    if (st.phase === 'rise') {
      const r = st.rocket;
      r.t += dt; r.x += r.vx * dt; r.y += r.vy * dt;
      r.emit += SHELL_TRAIL_PER_S * dt;
      while (r.emit >= 1) {
        r.emit -= 1;
        this.spawn(r.x + (Math.random() - 0.5) * 0.012, r.y, (Math.random() - 0.5) * 0.25, -0.1 - Math.random() * 0.35,
                   0.30 + Math.random() * 0.5, 0.95 + Math.random() * 0.5, 0, 0);
      }
      this.spawn(r.x, r.y, 0, 0, 0.022, 2.0, 0, 0.30);
      if (r.t >= r.dur) {
        // THE BREAK: an ordinary spherical burst - every star its own random
        // direction - whose stars also know their letter.
        const n = this._titleCount;
        const life = TITLE_FORMED + st.hold + 0.5;
        for (let k = 0; k < n; k++) {
          const a = Math.random() * Math.PI * 2;
          const v = 1.6 + Math.random() * 1.2;
          const i = this.spawn(st.bx, st.by, Math.cos(a) * v, Math.sin(a) * v, life,
                               1.35 + Math.random() * 0.5, 2, 0, 0.6, 0);
          this._titleIdx[i] = k;
        }
        this._flashThisFrame = Math.max(this._flashThisFrame, 0.35);
        st.phase = 'form'; st.t = 0;
      }
      U.x = 0; U.w = 0;
      return;
    }
    st.t += dt;
    if (st.phase === 'form' || st.phase === 'hold') {
      const k = Math.min(1, Math.max(0, (st.t - TITLE_FREE) / (TITLE_FORMED - TITLE_FREE)));
      const pull = k * k * (3 - 2 * k);
      U.x = pull; U.w = pull;
      if (st.t >= TITLE_FORMED) st.phase = 'hold';
      if (st.t >= TITLE_FORMED + st.hold) this._releaseTitle();
      return;
    }
    if (st.phase === 'fall') {
      U.x = 0; U.w = 0;
      if (st.t >= TITLE_FALL) { st.phase = 'idle'; }
    }
  }
  // Gravity takes over: every star still alive is put exactly where the
  // shader was drawing it, at rest, with a short life and a golden shed.
  _releaseTitle() {
    const st = this._titleState;
    const n = this._alive, idx = this._titleIdx, T = this._titleTargets;
    for (let i = 0; i < n; i++) {
      const k = idx[i];
      if (k < 0 || this._life[i] <= 0) continue;
      const x = T[k * 2], y = T[k * 2 + 1];
      this._pos[i * 2] = x; this._pos[i * 2 + 1] = y;
      this._prev[i * 2] = x + (Math.random() - 0.5) * 0.002;
      this._prev[i * 2 + 1] = y + Math.random() * 0.004;
      const L = TITLE_FALL * (0.75 + Math.random() * 0.25);
      this._life[i] = L; this._invLife[i] = 1 / L;
      this._grav[i] = 1.2;
      // no shed: fragments would take the shell colour and outnumber the rain
      this._shed[i] = 0;
    }
    st.phase = 'fall'; st.t = 0;
    this._shared.uTitle.value.x = 0; this._shared.uTitle.value.w = 0;
  }

  dispose() {
    // _flames is in this list on purpose: it is added to the scene only when
    // the layout asks for flame columns, and left out of the disposal it
    // leaked a burnt-in column into every visualizer that followed.
    for (const o of [this._sparks, this._mirror, this._trails, this._smoke, this._flames]) {
      if (!o) continue;
      o.onBeforeRender = null;
      if (this._scene) this._scene.remove(o);
    }
    if (this._geo) this._geo.dispose();
    if (this._titleTex) this._titleTex.dispose();
    if (this._trailGeo) this._trailGeo.dispose();
    if (this._smokeGeo) this._smokeGeo.dispose();
    if (this._flameGeo) this._flameGeo.dispose();
    for (const m of [this._sparkMat, this._mirrorMat, this._trailMat, this._smokeMat, this._flameMat]) {
      if (m) m.dispose();
    }
    this._sparks = this._mirror = this._trails = this._smoke = this._flames = null;
  }
}
